import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import i18next, { t } from "i18next";
import "./Reportsblock.css";

const REPORTS_PER_PAGE = 10;

const TEXT = {
  uk: {
    eyebrow: "Прозорість та результати",
    title: "Звіти про діяльність",
    intro:
      "Повна хронологія реалізації проєктів Благодійного фонду «Небо України»: від перших передач допомоги до підсумкових результатів.",

    projectsButton: "Переглянути проєкти",
    reportsButton: "Усі звіти",

    projectsTitle: "Проєкти та результати",
    projectsDescription:
      "Кожна картка об’єднує загальну інформацію про проєкт і всі пов’язані з ним проміжні та підсумкові звіти.",

    archiveTitle: "Архів звітів",
    archiveDescription:
      "Передачі допомоги, виїзди, виконані роботи, проміжні результати та підсумкові звіти.",

    projectsCount: "проєктів у звітності",
    reportsCount: "опублікованих звітів",
    regionsCount: "областей у звітах",
    latestYear: "останній рік публікацій",

    active: "Триває",
    completed: "Завершено",
    projectReports: "Усі звіти проєкту",
    reportsWord: "звітів",

    search: "Пошук за звітом, проєктом або громадою",
    allProjects: "Усі проєкти",
    allTypes: "Усі типи",
    interim: "Проміжні",
    final: "Підсумкові",
    allYears: "Усі роки",
    allRegions: "Усі області",
    reset: "Скинути",

    found: "Знайдено",
    newest: "Спочатку найновіші",

    openReport: "Переглянути звіт",
    linkPreparing: "Посилання готується",

    interimReport: "Проміжний звіт",
    finalReport: "Підсумковий звіт",

    noReports: "Звітів не знайдено",
    noReportsHint: "Змініть пошук або вибрані фільтри.",

    loading: "Завантажуємо звіти…",
    error: "Не вдалося завантажити звіти. Оновіть сторінку.",

    previous: "Назад",
    next: "Далі",
    page: "Сторінка",
    of: "з",

    filteredProject: "Показані звіти проєкту:",
    showAll: "Показати всі",

    generalProject: "Загальна звітність фонду",
    generalProjectDescription:
      "Звіти, які ще не прив’язані до окремої картки проєкту в базі даних.",

    noProjectDescription: "Опис проєкту буде додано.",
    photo: "фото",
  },

  en: {
    eyebrow: "Transparency and results",
    title: "Activity reports",
    intro:
      "A complete timeline of Sky of Ukraine Foundation projects, from the first aid deliveries to final outcomes.",

    projectsButton: "View projects",
    reportsButton: "All reports",

    projectsTitle: "Projects and outcomes",
    projectsDescription:
      "Each card combines general project information with all related interim and final reports.",

    archiveTitle: "Reports archive",
    archiveDescription:
      "Aid deliveries, field visits, completed work, interim outcomes and final reports.",

    projectsCount: "projects in reporting",
    reportsCount: "published reports",
    regionsCount: "regions covered",
    latestYear: "latest publication year",

    active: "Active",
    completed: "Completed",
    projectReports: "All project reports",
    reportsWord: "reports",

    search: "Search by report, project or community",
    allProjects: "All projects",
    allTypes: "All types",
    interim: "Interim",
    final: "Final",
    allYears: "All years",
    allRegions: "All regions",
    reset: "Reset",

    found: "Found",
    newest: "Newest first",

    openReport: "View report",
    linkPreparing: "Link is being prepared",

    interimReport: "Interim report",
    finalReport: "Final report",

    noReports: "No reports found",
    noReportsHint: "Change the search query or selected filters.",

    loading: "Loading reports…",
    error: "Unable to load reports. Refresh the page.",

    previous: "Previous",
    next: "Next",
    page: "Page",
    of: "of",

    filteredProject: "Showing reports for:",
    showAll: "Show all",

    generalProject: "General foundation reporting",
    generalProjectDescription:
      "Reports that have not yet been assigned to a dedicated project card in the database.",

    noProjectDescription: "Project description will be added.",
    photo: "photos",
  },
};

const MONTHS = {
  січень: 0,
  січня: 0,
  january: 0,

  лютий: 1,
  лютого: 1,
  february: 1,

  березень: 2,
  березня: 2,
  march: 2,

  квітень: 3,
  квітня: 3,
  april: 3,

  травень: 4,
  травня: 4,
  may: 4,

  червень: 5,
  червня: 5,
  june: 5,

  липень: 6,
  липня: 6,
  july: 6,

  серпень: 7,
  серпня: 7,
  august: 7,

  вересень: 8,
  вересня: 8,
  september: 8,

  жовтень: 9,
  жовтня: 9,
  october: 9,

  листопад: 10,
  листопада: 10,
  november: 10,

  грудень: 11,
  грудня: 11,
  december: 11,
};

function getLanguage() {
  const i18nextLanguage = String(i18next.language || "").toLowerCase();

  let savedLanguage = "";

  if (typeof window !== "undefined") {
    savedLanguage = String(
      window.localStorage.getItem("language") || ""
    ).toLowerCase();
  }

  if (
    i18nextLanguage.startsWith("en") ||
    savedLanguage.startsWith("en")
  ) {
    return "en";
  }

  return "uk";
}

function translate(value) {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value !== "string") {
    return String(value);
  }

  return t(value);
}

function extractApiArray(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  return [];
}

function createSafeDate(year, month, day) {
  const date = new Date(year, month, day);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date;
}

function parseDate(value) {
  if (!value) {
    return null;
  }

  const rawValue = String(value).trim();

  if (!rawValue) {
    return null;
  }

  /*
   * Підтримує:
   * 2023-02-15
   * 2023-02-15T12:00:00
   */
  const isoMatch = rawValue.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/
  );

  if (isoMatch) {
    return createSafeDate(
      Number(isoMatch[1]),
      Number(isoMatch[2]) - 1,
      Number(isoMatch[3])
    );
  }

  /*
   * Підтримує:
   * 15.02.2023
   * 15/02/2023
   * 15-02-2023
   */
  const numericMatch = rawValue.match(
    /^(\d{1,2})[./-](\d{1,2})[./-]((?:19|20)\d{2})$/
  );

  if (numericMatch) {
    return createSafeDate(
      Number(numericMatch[3]),
      Number(numericMatch[2]) - 1,
      Number(numericMatch[1])
    );
  }

  /*
   * Підтримує старі перекладені дати:
   * 15 лютого 2023
   * 15 Лютого, 2023
   * February 15, 2023
   */
  const normalizedValue = rawValue
    .toLowerCase()
    .replace(/[,.]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const yearMatch = normalizedValue.match(
    /\b((?:19|20)\d{2})\b/
  );

  if (!yearMatch) {
    return null;
  }

  const monthEntry = Object.entries(MONTHS).find(
    ([monthName]) => normalizedValue.includes(monthName)
  );

  if (!monthEntry) {
    return null;
  }

  const valueWithoutYear = normalizedValue.replace(
    yearMatch[1],
    " "
  );

  const dayMatch = valueWithoutYear.match(
    /\b([1-9]|[12]\d|3[01])\b/
  );

  return createSafeDate(
    Number(yearMatch[1]),
    monthEntry[1],
    dayMatch ? Number(dayMatch[1]) : 1
  );
}

function isMachineDate(value) {
  const rawValue = String(value || "").trim();

  return (
    /^\d{4}-\d{2}-\d{2}(?:[T\s].*)?$/.test(rawValue) ||
    /^\d{1,2}[./-]\d{1,2}[./-](?:19|20)\d{2}$/.test(
      rawValue
    )
  );
}

function formatDisplayDate(value, language) {
  if (!value) {
    return "";
  }

  /*
   * Старе поле report.data може містити ключ перекладу:
   * report data 99
   *
   * Воно спочатку перекладається через i18next.
   */
  const translatedValue = translate(value);

  /*
   * Якщо це не технічна дата, повертаємо старий перекладений текст.
   * Завдяки цьому JavaScript більше не перетворює "99" на 1999 рік.
   */
  if (!isMachineDate(value)) {
    return translatedValue;
  }

  const parsedDate = parseDate(value);

  if (!parsedDate) {
    return translatedValue;
  }

  return new Intl.DateTimeFormat(
    language === "uk" ? "uk-UA" : "en-GB",
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }
  ).format(parsedDate);
}

function getReportDate(report) {
  const publicationDate = parseDate(
    report.publicationDate
  );

  if (publicationDate) {
    return publicationDate;
  }

  const translatedOldDate = parseDate(
    translate(report.displayDate)
  );

  if (translatedOldDate) {
    return translatedOldDate;
  }

  return parseDate(report.displayDate);
}

function getReportYear(report) {
  const parsedDate = getReportDate(report);

  if (parsedDate) {
    return String(parsedDate.getFullYear());
  }

  /*
   * Беремо тільки повний чотиризначний рік.
   * Значення 92, 93 або 99 роками не вважаються.
   */
  const translatedDate = translate(
    report.displayDate
  );

  const yearMatch = String(translatedDate).match(
    /\b((?:19|20)\d{2})\b/
  );

  return yearMatch ? yearMatch[1] : "";
}

function createSlug(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-zа-яіїєґ0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "");
}

function createImageUrl(value) {
  if (!value) {
    return "";
  }

  if (/^(https?:|data:|blob:)/i.test(value)) {
    return value;
  }

  const backendUrl = String(
    process.env.REACT_APP_BACKEND_LINK || ""
  ).replace(/\/$/, "");

  return `${backendUrl}/${String(value).replace(
    /^\//,
    ""
  )}`;
}

function normalizeStringArray(value) {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map(translate);
  }

  return String(value)
    .split(/\n|;/)
    .map((item) => translate(item.trim()))
    .filter(Boolean);
}

function getTheme(...values) {
  const text = values
    .join(" ")
    .toLowerCase();

  if (
    /вода|водо|свердлов|очищ|water/.test(
      text
    )
  ) {
    return "water";
  }

  if (
    /тепл|енерг|дров|генератор|соняч|solar|power/.test(
      text
    )
  ) {
    return "energy";
  }

  if (
    /житл|ремонт|віднов|housing|roof/.test(
      text
    )
  ) {
    return "housing";
  }

  if (
    /мед|лікар|hospital|medical/.test(
      text
    )
  ) {
    return "medical";
  }

  return "humanitarian";
}

function normalizeProject(project, index) {
  const title = translate(
    project.title ??
      project.name ??
      project.project_name ??
      ""
  );

  const id = String(
    project.id ??
      project.project_id ??
      project.slug ??
      createSlug(title) ??
      `project-${index}`
  );

  const period = translate(
    project.project_duration ??
      project.period ??
      project.duration ??
      ""
  );

  const statusText = String(
    project.status ?? period
  ).toLowerCase();

  return {
    id,
    title:
      title || `Project ${index + 1}`,

    description: translate(
      project.short_description ??
        project.description ??
        project.meta ??
        project.goal ??
        ""
    ),

    category: translate(
      project.category ??
        project.direction ??
        ""
    ),

    region: translate(
      project.region ??
        project.location ??
        ""
    ),

    period,

    status:
      /заверш|complete|done/.test(statusText)
        ? "completed"
        : "active",

    theme: getTheme(
      title,
      project.description,
      project.meta,
      project.category
    ),
  };
}

function normalizeReport(
  report,
  index,
  apiProjects
) {
  const projectTitle = translate(
    report.project_title ??
      report.projectTitle ??
      report.project_name ??
      report.project?.title ??
      report.project?.name ??
      ""
  );

  const explicitProjectId =
    report.project_id ??
    report.projectId ??
    report.project?.id;

  let projectId =
    explicitProjectId === null ||
    explicitProjectId === undefined
      ? ""
      : String(explicitProjectId);

  if (!projectId && projectTitle) {
    const matchingProject = apiProjects.find(
      (project) =>
        createSlug(project.title) ===
        createSlug(projectTitle)
    );

    projectId = matchingProject
      ? matchingProject.id
      : `virtual-${createSlug(projectTitle)}`;
  }

  if (!projectId) {
    projectId = "general";
  }

  const title = translate(
    report.title ??
      report.name ??
      `Report ${index + 1}`
  );

  const description = translate(
    report.short_description ??
      report.description ??
      report.excerpt ??
      report.meta ??
      ""
  );

  const content = translate(
    report.content ??
      report.full_description ??
      report.body ??
      report.report_text ??
      description
  );

  const publicationDate =
    report.publicationDate ??
    report.publication_date ??
    report.date ??
    "";

  const displayDate =
    report.data ??
    report.display_date ??
    report.displayDate ??
    publicationDate;

  const reportType = String(
    report.report_type ??
      report.reportType ??
      report.type ??
      "interim"
  ).toLowerCase();

  const photos = [
    ...(Array.isArray(report.photos)
      ? report.photos
      : []),

    report.photo_1,
    report.photo_2,
    report.photo_3,
  ]
    .filter(Boolean)
    .map(createImageUrl)
    .filter(Boolean);

  return {
    id: String(
      report.id ??
        report.report_id ??
        `${projectId}-${index}`
    ),

    projectId,
    projectTitle,
    title,
    description,
    content,
    publicationDate,
    displayDate,

    type:
      /final|summary|підсум|загаль/.test(
        reportType
      )
        ? "final"
        : "interim",

    region: translate(
      report.region ??
        report.location ??
        ""
    ),

    community: translate(
      report.community ??
        report.city ??
        report.settlement ??
        ""
    ),

    photos,

    results: normalizeStringArray(
      report.results ??
        report.key_results ??
        report.result
    ),

    link:
      report.link ??
      report.url ??
      report.report_url ??
      "",

    theme: getTheme(
      title,
      description,
      report.category,
      report.direction
    ),
  };
}

function createPagination(
  currentPage,
  totalPages
) {
  if (totalPages <= 7) {
    return Array.from(
      { length: totalPages },
      (_, index) => index + 1
    );
  }

  const pages = [1];

  const start = Math.max(
    2,
    currentPage - 1
  );

  const end = Math.min(
    totalPages - 1,
    currentPage + 1
  );

  if (start > 2) {
    pages.push("left");
  }

  for (
    let page = start;
    page <= end;
    page += 1
  ) {
    pages.push(page);
  }

  if (end < totalPages - 1) {
    pages.push("right");
  }

  pages.push(totalPages);

  return pages;
}

export default function Reportsblock() {
  const language = getLanguage();
  const copy = TEXT[language];

  const archiveRef = useRef(null);

  const [rawReports, setRawReports] =
    useState([]);

  const [rawProjects, setRawProjects] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState(false);

  const [search, setSearch] =
    useState("");

  const [projectFilter, setProjectFilter] =
    useState("all");

  const [typeFilter, setTypeFilter] =
    useState("all");

  const [yearFilter, setYearFilter] =
    useState("all");

  const [regionFilter, setRegionFilter] =
    useState("all");

  const [currentPage, setCurrentPage] =
    useState(1);

  useEffect(() => {
    let mounted = true;

    const backendUrl = String(
      process.env.REACT_APP_BACKEND_LINK || ""
    ).replace(/\/$/, "");

    Promise.allSettled([
      axios.get(`${backendUrl}/reports/`),
      axios.get(`${backendUrl}/projects/`),
    ])
      .then(
        ([
          reportsResponse,
          projectsResponse,
        ]) => {
          if (!mounted) {
            return;
          }

          if (
            reportsResponse.status ===
            "fulfilled"
          ) {
            setRawReports(
              extractApiArray(
                reportsResponse.value.data
              )
            );
          } else {
            setError(true);
          }

          if (
            projectsResponse.status ===
            "fulfilled"
          ) {
            setRawProjects(
              extractApiArray(
                projectsResponse.value.data
              )
            );
          }
        }
      )
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const apiProjects = useMemo(
    () =>
      rawProjects.map(
        normalizeProject
      ),
    [rawProjects]
  );

  const reports = useMemo(() => {
    return rawReports
      .map((report, index) =>
        normalizeReport(
          report,
          index,
          apiProjects
        )
      )
      .sort((firstReport, secondReport) => {
        const firstDate =
          getReportDate(firstReport)
            ?.getTime() || 0;

        const secondDate =
          getReportDate(secondReport)
            ?.getTime() || 0;

        return secondDate - firstDate;
      });
  }, [rawReports, apiProjects]);

  const reportCountByProject =
    useMemo(() => {
      const counts = new Map();

      reports.forEach((report) => {
        counts.set(
          report.projectId,
          (counts.get(report.projectId) ||
            0) + 1
        );
      });

      return counts;
    }, [reports]);

  const projects = useMemo(() => {
    const result = [...apiProjects];

    const knownProjectIds = new Set(
      result.map((project) => project.id)
    );

    reports.forEach((report) => {
      if (
        knownProjectIds.has(report.projectId)
      ) {
        return;
      }

      result.push({
        id: report.projectId,

        title:
          report.projectId === "general"
            ? copy.generalProject
            : report.projectTitle ||
              copy.generalProject,

        description:
          report.projectId === "general"
            ? copy.generalProjectDescription
            : report.description ||
              copy.generalProjectDescription,

        category: "",
        region: report.region,
        period: "",
        status: "active",
        theme: report.theme,
      });

      knownProjectIds.add(
        report.projectId
      );
    });

    return result.filter(
      (project) =>
        (reportCountByProject.get(
          project.id
        ) || 0) > 0
    );
  }, [
    apiProjects,
    reports,
    reportCountByProject,
    copy,
  ]);

  const projectMap = useMemo(
    () =>
      new Map(
        projects.map((project) => [
          project.id,
          project,
        ])
      ),
    [projects]
  );

  const years = useMemo(() => {
    return [
      ...new Set(
        reports
          .map(getReportYear)
          .filter(Boolean)
      ),
    ].sort(
      (firstYear, secondYear) =>
        Number(secondYear) -
        Number(firstYear)
    );
  }, [reports]);

  const regions = useMemo(() => {
    return [
      ...new Set(
        reports
          .map(
            (report) => report.region
          )
          .filter(Boolean)
      ),
    ].sort((firstRegion, secondRegion) =>
      firstRegion.localeCompare(
        secondRegion
      )
    );
  }, [reports]);

  const filteredReports = useMemo(() => {
    const normalizedSearch = search
      .trim()
      .toLowerCase();

    return reports.filter((report) => {
      const projectTitle =
        projectMap.get(report.projectId)
          ?.title ||
        report.projectTitle ||
        copy.generalProject;

      const searchableText = [
        report.title,
        report.description,
        report.content,
        projectTitle,
        report.region,
        report.community,
      ]
        .join(" ")
        .toLowerCase();

      return (
        (!normalizedSearch ||
          searchableText.includes(
            normalizedSearch
          )) &&

        (projectFilter === "all" ||
          report.projectId ===
            projectFilter) &&

        (typeFilter === "all" ||
          report.type === typeFilter) &&

        (yearFilter === "all" ||
          getReportYear(report) ===
            yearFilter) &&

        (regionFilter === "all" ||
          report.region === regionFilter)
      );
    });
  }, [
    reports,
    search,
    projectFilter,
    typeFilter,
    yearFilter,
    regionFilter,
    projectMap,
    copy.generalProject,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredReports.length /
        REPORTS_PER_PAGE
    )
  );

  const currentReports =
    filteredReports.slice(
      (currentPage - 1) *
        REPORTS_PER_PAGE,

      currentPage *
        REPORTS_PER_PAGE
    );

  const pagination = createPagination(
    currentPage,
    totalPages
  );

  const activeProject =
    projectMap.get(projectFilter);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    projectFilter,
    typeFilter,
    yearFilter,
    regionFilter,
  ]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  function resetFilters() {
    setSearch("");
    setProjectFilter("all");
    setTypeFilter("all");
    setYearFilter("all");
    setRegionFilter("all");
  }

  function showProjectReports(projectId) {
    setProjectFilter(
      String(projectId)
    );

    setCurrentPage(1);

    window.setTimeout(() => {
      archiveRef.current?.scrollIntoView({
        behavior: "smooth",
      });
    }, 0);
  }

  function changePage(page) {
    setCurrentPage(page);

    window.setTimeout(() => {
      archiveRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 0);
  }

  return (
    <main className="sky-reports">
      <section className="sr-hero">
        <div className="sr-hero-shape" />

        <div className="sr-container sr-hero-grid">
          <div>
            <span className="sr-eyebrow">
              <i />
              {copy.eyebrow}
            </span>

            <h1>{copy.title}</h1>

            <p>{copy.intro}</p>

            <div className="sr-hero-actions">
              <a
                href="#reports-projects"
                className="sr-button sr-button-yellow"
              >
                {copy.projectsButton}
              </a>

              <a
                href="#reports-archive"
                className="sr-button sr-button-dark"
              >
                {copy.reportsButton}
              </a>
            </div>
          </div>

          <aside className="sr-hero-card">
            <span>{copy.eyebrow}</span>

            <strong>{reports.length}</strong>

            <p>{copy.reportsCount}</p>

            <div>
              <b>{projects.length}</b>
              <small>{copy.projectsCount}</small>
            </div>
          </aside>
        </div>
      </section>

      <section className="sr-statistics">
        <div className="sr-container sr-statistics-grid">
          <article>
            <strong>{projects.length}</strong>
            <span>{copy.projectsCount}</span>
          </article>

          <article>
            <strong>{reports.length}</strong>
            <span>{copy.reportsCount}</span>
          </article>

          <article>
            <strong>{regions.length}</strong>
            <span>{copy.regionsCount}</span>
          </article>

          <article>
            <strong>{years[0] || "—"}</strong>
            <span>{copy.latestYear}</span>
          </article>
        </div>
      </section>

      <section
        className="sr-section sr-projects-section"
        id="reports-projects"
      >
        <div className="sr-container">
          <div className="sr-section-heading">
            <div>
              <span>{copy.eyebrow}</span>
              <h2>{copy.projectsTitle}</h2>
            </div>

            <p>{copy.projectsDescription}</p>
          </div>

          {loading ? (
            <div className="sr-state">
              {copy.loading}
            </div>
          ) : projects.length > 0 ? (
            <div className="sr-projects-grid">
              {projects.map((project) => (
                <article
                  className={`sr-project-card sr-theme-${project.theme}`}
                  key={project.id}
                >
                  <div className="sr-project-accent" />

                  <div className="sr-project-content">
                    <div className="sr-project-top">
                      <span>
                        {project.category ||
                          project.region ||
                          copy.projectsTitle}
                      </span>

                      <b
                        className={
                          project.status
                        }
                      >
                        {project.status ===
                        "completed"
                          ? copy.completed
                          : copy.active}
                      </b>
                    </div>

                    <h3>{project.title}</h3>

                    <p>
                      {project.description ||
                        copy.noProjectDescription}
                    </p>

                    {(project.period ||
                      project.region) && (
                      <div className="sr-project-facts">
                        {project.period && (
                          <span>
                            {project.period}
                          </span>
                        )}

                        {project.region && (
                          <span>
                            {project.region}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="sr-project-footer">
                      <button
                        type="button"
                        onClick={() =>
                          showProjectReports(
                            project.id
                          )
                        }
                      >
                        {copy.projectReports}
                      </button>

                      <span>
                        {reportCountByProject.get(
                          project.id
                        ) || 0}{" "}
                        {copy.reportsWord}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="sr-state">
              {copy.noReports}
            </div>
          )}
        </div>
      </section>

      <section
        className="sr-section sr-archive-section"
        id="reports-archive"
        ref={archiveRef}
      >
        <div className="sr-container">
          <div className="sr-section-heading">
            <div>
              <span>{copy.eyebrow}</span>
              <h2>{copy.archiveTitle}</h2>
            </div>

            <p>{copy.archiveDescription}</p>
          </div>

          <div className="sr-filter-box">
            <div className="sr-filter-grid">
              <label className="sr-control sr-search">
                <svg
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    cx="11"
                    cy="11"
                    r="7"
                  />

                  <path d="m20 20-4-4" />
                </svg>

                <input
                  type="search"
                  value={search}
                  onChange={(event) =>
                    setSearch(
                      event.target.value
                    )
                  }
                  placeholder={copy.search}
                />
              </label>

              <label className="sr-control">
                <select
                  value={projectFilter}
                  onChange={(event) =>
                    setProjectFilter(
                      event.target.value
                    )
                  }
                >
                  <option value="all">
                    {copy.allProjects}
                  </option>

                  {projects.map(
                    (project) => (
                      <option
                        key={project.id}
                        value={project.id}
                      >
                        {project.title}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label className="sr-control">
                <select
                  value={typeFilter}
                  onChange={(event) =>
                    setTypeFilter(
                      event.target.value
                    )
                  }
                >
                  <option value="all">
                    {copy.allTypes}
                  </option>

                  <option value="interim">
                    {copy.interim}
                  </option>

                  <option value="final">
                    {copy.final}
                  </option>
                </select>
              </label>

              <label className="sr-control">
                <select
                  value={yearFilter}
                  onChange={(event) =>
                    setYearFilter(
                      event.target.value
                    )
                  }
                >
                  <option value="all">
                    {copy.allYears}
                  </option>

                  {years.map((year) => (
                    <option
                      key={year}
                      value={year}
                    >
                      {year}
                    </option>
                  ))}
                </select>
              </label>

              <label className="sr-control">
                <select
                  value={regionFilter}
                  onChange={(event) =>
                    setRegionFilter(
                      event.target.value
                    )
                  }
                >
                  <option value="all">
                    {copy.allRegions}
                  </option>

                  {regions.map((region) => (
                    <option
                      key={region}
                      value={region}
                    >
                      {region}
                    </option>
                  ))}
                </select>
              </label>

              <button
                type="button"
                className="sr-button sr-button-dark"
                onClick={resetFilters}
              >
                {copy.reset}
              </button>
            </div>

            {activeProject && (
              <div className="sr-active-project">
                <span>
                  {copy.filteredProject}{" "}
                  <strong>
                    {activeProject.title}
                  </strong>
                </span>

                <button
                  type="button"
                  onClick={() =>
                    setProjectFilter("all")
                  }
                >
                  {copy.showAll}
                </button>
              </div>
            )}
          </div>

          <div className="sr-archive-meta">
            <span>
              {copy.found}:{" "}
              <strong>
                {filteredReports.length}
              </strong>{" "}
              {copy.reportsWord}
            </span>

            <span>{copy.newest}</span>
          </div>

          {loading ? (
            <div className="sr-state">
              {copy.loading}
            </div>
          ) : error && reports.length === 0 ? (
            <div className="sr-state sr-state-error">
              {copy.error}
            </div>
          ) : currentReports.length > 0 ? (
            <div className="sr-reports-grid">
              {currentReports.map(
                (report) => {
                  const projectTitle =
                    projectMap.get(
                      report.projectId
                    )?.title ||
                    report.projectTitle ||
                    copy.generalProject;

                  const coverPhoto =
                    report.photos[0];

                  return (
                    <article
                      className="sr-report-card"
                      key={report.id}
                    >
                      <div
                        className={`sr-report-cover sr-cover-${report.theme} ${
                          coverPhoto
                            ? "has-image"
                            : ""
                        }`}
                      >
                        {coverPhoto && (
                          <img
                            src={coverPhoto}
                            alt={report.title}
                            loading="lazy"
                          />
                        )}

                        <span
                          className={`sr-report-type ${report.type}`}
                        >
                          {report.type ===
                          "final"
                            ? copy.finalReport
                            : copy.interimReport}
                        </span>
                      </div>

                      <div className="sr-report-content">
                        <span className="sr-report-project">
                          {projectTitle}
                        </span>

                        <h3>{report.title}</h3>

                        <p>
                          {report.description ||
                            report.content}
                        </p>

                        <div className="sr-report-tags">
                          {report.displayDate && (
                            <span className="date">
                              {formatDisplayDate(
                                report.displayDate,
                                language
                              )}
                            </span>
                          )}

                          {report.region && (
                            <span>
                              {report.region}
                            </span>
                          )}

                          {report.community && (
                            <span>
                              {report.community}
                            </span>
                          )}
                        </div>

                        <div className="sr-report-footer">
  {report.link ? (
    <a
      href={report.link}
      target="_blank"
      rel="noopener noreferrer"
    >
      {copy.openReport}
    </a>
  ) : (
    <span className="sr-disabled-link">
      {copy.linkPreparing}
    </span>
  )}

  <span className="sr-report-arrow" aria-hidden="true">
    →
  </span>
</div>
                      </div>
                    </article>
                  );
                }
              )}
            </div>
          ) : (
            <div className="sr-state">
              <strong>
                {copy.noReports}
              </strong>

              <span>
                {copy.noReportsHint}
              </span>
            </div>
          )}

          {filteredReports.length >
            REPORTS_PER_PAGE && (
            <div className="sr-pagination-wrapper">
              <span>
                {copy.page} {currentPage}{" "}
                {copy.of} {totalPages}
              </span>

              <nav className="sr-pagination">
                <button
                  type="button"
                  disabled={
                    currentPage === 1
                  }
                  onClick={() =>
                    changePage(
                      currentPage - 1
                    )
                  }
                >
                  {copy.previous}
                </button>

                {pagination.map((page) =>
                  typeof page ===
                  "number" ? (
                    <button
                      type="button"
                      key={page}
                      className={
                        page === currentPage
                          ? "active"
                          : ""
                      }
                      onClick={() =>
                        changePage(page)
                      }
                    >
                      {page}
                    </button>
                  ) : (
                    <i key={page}>…</i>
                  )
                )}

                <button
                  type="button"
                  disabled={
                    currentPage ===
                    totalPages
                  }
                  onClick={() =>
                    changePage(
                      currentPage + 1
                    )
                  }
                >
                  {copy.next}
                </button>
              </nav>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}